// useOrgLogo - the organization's report logo, as something an <img> can show.
//
// The logo is bytes behind a session-scoped route, so it travels as a Blob
// through TanStack Query rather than as an <img src> pointing at the API:
// the printed document's readiness gate waits on in-flight queries, which is
// what makes "the logo has landed" part of "the PDF is ready" with nothing
// extra to track. An object URL is minted per Blob and revoked when it is
// replaced or the consumer goes away.

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

/** The query key for one org's logo; the dashboard card invalidates it. */
export function orgLogoKey(orgId: string) {
  return ['org:logo', orgId] as const;
}

export interface OrgLogoState {
  /** An object URL for the logo, or null when the org has none or the answer
   *  has not landed yet. */
  url: string | null;
  /** True until the first answer lands. False for a null org. */
  isPending: boolean;
}

export function useOrgLogo(orgId: string | null): OrgLogoState {
  const query = useQuery({
    queryKey: ['org:logo', orgId],
    queryFn: () => api.orgLogo(orgId as string),
    enabled: orgId !== null,
    // Replaced only through the card, which invalidates this key.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  const blob = query.data ?? null;

  // State rather than a memo: a memo is re-run under StrictMode's double
  // render and would leak the first URL every time.
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);

  // A disabled query reports `pending` forever; for a null org the honest
  // answer is that there is nothing to wait for.
  return { url, isPending: orgId !== null && query.isPending };
}
